/**
 * Firebase Web SDK initialisation — scoped to the admin panel Google Sign-In.
 *
 * The user-facing app (owners / shops) authenticates via the existing
 * email-password JWT flow and does NOT touch this module. This file is only
 * imported from /admin/login.
 *
 * Config values come from REACT_APP_FIREBASE_* env vars so the deployment can
 * wire its own Firebase project without code changes. `apiKey` and `appId`
 * MUST be supplied by the operator after registering a Web App in
 * Firebase Console → Project Settings → General → Your apps → Web app.
 *
 * Note: the `apiKey` for the Firebase Web SDK is NOT a secret — it identifies
 * the project and is enforced via authorized-domains + Firebase security
 * rules, not via secrecy. It is safe to expose to the browser.
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    (process.env.REACT_APP_FIREBASE_PROJECT_ID
      ? `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.firebaseapp.com`
      : undefined),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    (process.env.REACT_APP_FIREBASE_PROJECT_ID
      ? `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.appspot.com`
      : undefined),
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export function isFirebaseConfigured() {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);
}

export function getFirebaseApp() {
  if (!isFirebaseConfigured()) {
    throw new Error(
      "Firebase Web SDK is not configured. Set REACT_APP_FIREBASE_API_KEY, " +
        "REACT_APP_FIREBASE_PROJECT_ID, and REACT_APP_FIREBASE_APP_ID in frontend/.env"
    );
  }
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export function getFirebaseAuth() {
  return getAuth(getFirebaseApp());
}

/**
 * Pops the Google account chooser, returns the Firebase ID token on success.
 * Caller is responsible for sending the token to the backend and handling
 * 401/403 responses.
 */
export async function signInWithGoogleAndGetIdToken() {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken(/* forceRefresh */ true);
  return {
    idToken,
    email: result.user.email,
    displayName: result.user.displayName,
    photoURL: result.user.photoURL,
  };
}

export async function signOutFirebase() {
  try {
    await signOut(getFirebaseAuth());
  } catch {
    /* no firebase session, fine */
  }
}
